import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import {
  Smile, ArrowRight, ArrowLeft, Sparkles, Check, MessageCircle, RotateCcw, AlertCircle,
} from "lucide-react";

const WA_NUMBER = "5217712840787";

const QUESTIONS = [
  {
    id: "edad",
    title: "¿Cuántos años tiene tu hijo o hija?",
    type: "single",
    options: [
      { v: "3-5", label: "Entre 3 y 5 años" },
      { v: "6-8", label: "Entre 6 y 8 años" },
      { v: "9-12", label: "Entre 9 y 12 años" },
      { v: "13+", label: "13 años o más" },
    ],
  },
  {
    id: "respiracion",
    title: "¿Respira por la boca al dormir o ronca?",
    type: "single",
    options: [
      { v: 0, label: "No, respira por la nariz tranquilamente" },
      { v: 1, label: "A veces, sólo cuando está congestionado" },
      { v: 2, label: "Sí, casi siempre" },
      { v: 3, label: "Sí, y además ronca fuerte" },
    ],
  },
  {
    id: "habitos",
    title: "¿Tiene algún hábito oral persistente?",
    type: "single",
    options: [
      { v: 0, label: "Ninguno" },
      { v: 1, label: "Se chupa el dedo o usa chupón ocasionalmente" },
      { v: 2, label: "Mete la lengua entre los dientes o se come las uñas" },
      { v: 3, label: "Varios hábitos al mismo tiempo" },
    ],
  },
  {
    id: "senales",
    title: "¿Qué señales has notado? (puedes marcar varias)",
    type: "multi",
    options: [
      { v: "apinamiento", label: "Dientes apiñados o chuecos" },
      { v: "mordida", label: "Mordida cruzada, abierta o profunda" },
      { v: "mandibula", label: "Mandíbula desviada o adelantada" },
      { v: "masticar", label: "Dificultad para masticar duro" },
      { v: "hablar", label: "Problemas para pronunciar algunas letras" },
      { v: "facial", label: "Rostro asimétrico o muy alargado" },
      { v: "ninguna", label: "Ninguna por ahora, sólo busco prevención" },
    ],
  },
  {
    id: "valoracion",
    title: "¿Ha tenido una valoración con ortodoncista antes?",
    type: "single",
    options: [
      { v: 0, label: "No, sería la primera" },
      { v: 1, label: "Sí, pero hace más de 2 años" },
      { v: 2, label: "Sí, recientemente y me dijeron que esperara" },
      { v: 3, label: "Sí y ya está en tratamiento" },
    ],
  },
];

const TONES = {
  ahora: {
    title: "Es el momento ideal para actuar.",
    body: "Las señales que describes son indicios claros de que tu hijo se beneficiaría de una valoración de ortopedia funcional. La buena noticia: a esta edad, el tratamiento es más rápido, menos invasivo y de menor costo que esperar.",
  },
  pronto: {
    title: "Vale la pena una valoración pronto.",
    body: "Hay señales que indican que podríamos prevenir tratamientos más complejos en el futuro. Una consulta temprana cuesta una fracción de lo que costaría una corrección a los 15 años.",
  },
  vigilar: {
    title: "Estás siendo una buena prevención.",
    body: "Por ahora no se detectan señales urgentes, pero una valoración anual de seguimiento puede identificar a tiempo cualquier cambio en el desarrollo óseo y dental.",
  },
};

const computeKidsResult = (answers) => {
  const score =
    (answers.respiracion?.v || 0) +
    (answers.habitos?.v || 0) +
    ((answers.senales?.v || []).filter((s) => s !== "ninguna").length * 1.5);
  const tone = score >= 5 ? TONES.ahora : score >= 2 ? TONES.pronto : TONES.vigilar;
  const recs = [];
  const senales = answers.senales?.v || [];
  if (answers.respiracion?.v >= 2) recs.push("Evaluación de respiración bucal y función nasal");
  if (answers.habitos?.v >= 1) recs.push("Corrección de hábitos orales (succión digital, deglución atípica)");
  if (senales.includes("mordida")) recs.push("Ortopedia funcional para corrección de mordida");
  if (senales.includes("apinamiento")) recs.push("Expansión maxilar para crear espacio");
  if (senales.includes("mandibula")) recs.push("Aparato Twin Block para desarrollo mandibular");
  if (senales.includes("facial")) recs.push("Análisis cefalométrico de crecimiento facial");
  if (recs.length === 0) recs.push("Valoración integral de crecimiento y desarrollo (preventiva)");
  return { tone, recs, score };
};

const buildWaKids = (result, edad) => {
  const recs = (result?.recs || []).join(" + ");
  const msg = `Hola Dra. Tania, hice el Quiz Infantil en su sitio web. Mi hijo/a tiene ${edad} años y los resultados sugieren: ${recs}. Me gustaría agendar una *valoración con el 15% de descuento*. ¿Qué horarios tienen?`;
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`;
};

export default function KidsQuiz({ open, onOpenChange }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const total = QUESTIONS.length;
  const isResult = step === total;
  const q = !isResult ? QUESTIONS[step] : null;
  const progress = Math.round(((isResult ? total : step) / total) * 100);

  const setAnswer = (val) => setAnswers((a) => ({ ...a, [q.id]: { v: val } }));
  const toggleMulti = (val) => {
    const cur = answers[q.id]?.v || [];
    setAnswer(cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val]);
  };
  const currentAns = q ? answers[q.id]?.v : undefined;
  const canNext = q?.type === "multi"
    ? Array.isArray(currentAns) && currentAns.length > 0
    : currentAns !== undefined && currentAns !== null;
  const reset = () => { setStep(0); setAnswers({}); };
  const result = isResult ? computeKidsResult(answers) : null;

  if (isResult && result) {
    api.post("/quiz/submit", {
      answers,
      areas: ["ortopedia_funcional_infantil"],
      treatments: result.recs,
      score: result.score,
      kind: "kids",
    }).catch(() => {});
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent translate="no" className="notranslate max-w-2xl p-0 overflow-hidden bg-white border-[#E6E2DC]" data-testid="kids-quiz-dialog">
        <div className="bg-[#1A1C1A] text-[#D9CDBC] px-8 py-6">
          <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#7A9180]">
            <Smile className="w-3.5 h-3.5" /> Quiz para padres · Ortopedia infantil
          </div>
          <h3 className="font-display text-2xl text-[#FAFAFA] mt-2">
            {isResult ? "Tu resultado" : `Pregunta ${step + 1} de ${total}`}
          </h3>
          <Progress value={progress} className="mt-4 h-1 bg-[#2a2b29]" />
        </div>

        <div className="p-8 max-h-[60vh] overflow-y-auto">
          {!isResult && (
            <div data-testid={`kids-quiz-step-${step}`}>
              <p className="font-display text-2xl text-[#1A1C1A] leading-snug mb-6">{q.title}</p>

              {q.type === "single" && (
                <div className="space-y-2.5">
                  {q.options.map((o) => {
                    const sel = answers[q.id]?.v === o.v;
                    return (
                      <button
                        key={String(o.v)}
                        onClick={() => setAnswer(o.v)}
                        data-testid={`kids-opt-${q.id}-${o.v}`}
                        className={`w-full text-left px-5 py-4 rounded-xl border transition-all flex items-center gap-3 ${sel ? "border-[#7A9180] bg-[#E6F0E8]" : "border-[#E6E2DC] hover:border-[#D9CDBC] bg-white"}`}
                      >
                        <span className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${sel ? "border-[#7A9180] bg-[#7A9180]" : "border-[#D9CDBC]"}`}>
                          {sel && <Check className="w-3 h-3 text-white" />}
                        </span>
                        <span className="text-sm text-[#1A1C1A]">{o.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {q.type === "multi" && (
                <div className="grid sm:grid-cols-2 gap-2.5">
                  {q.options.map((o) => {
                    const cur = answers[q.id]?.v || [];
                    const sel = cur.includes(o.v);
                    return (
                      <button
                        key={o.v}
                        onClick={() => toggleMulti(o.v)}
                        data-testid={`kids-opt-${q.id}-${o.v}`}
                        className={`text-left px-4 py-3.5 rounded-xl border transition-all flex items-center gap-3 ${sel ? "border-[#7A9180] bg-[#E6F0E8]" : "border-[#E6E2DC] hover:border-[#D9CDBC] bg-white"}`}
                      >
                        <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${sel ? "border-[#7A9180] bg-[#7A9180]" : "border-[#D9CDBC]"}`}>
                          {sel && <Check className="w-3 h-3 text-white" />}
                        </span>
                        <span className="text-xs text-[#1A1C1A] leading-tight">{o.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {isResult && (
            <div data-testid="kids-quiz-result">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#7A9180] mb-2">Mi recomendación</div>
              <h4 className="font-display text-3xl text-[#1A1C1A] leading-tight">{result.tone.title}</h4>
              <p className="mt-4 text-[15px] text-[#5C5E5C] leading-relaxed">{result.tone.body}</p>

              <div className="mt-7 bg-[#E6F0E8] rounded-2xl p-6 border border-[#7A9180]/30">
                <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#5d7a66] mb-3">
                  <Sparkles className="w-3.5 h-3.5" /> Tratamientos sugeridos a evaluar
                </div>
                <ul className="space-y-2">
                  {result.recs.map((r, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#1A1C1A]">
                      <Check className="w-4 h-4 text-[#7A9180] mt-0.5 shrink-0" /> <span>{r}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-[#5C5E5C] mt-4 italic">
                  Este resultado es orientativo. El diagnóstico definitivo se realiza únicamente con una valoración presencial.
                </p>
              </div>

              {result.score >= 5 && (
                <div className="mt-4 flex items-start gap-3 bg-[#F4DCDC]/40 rounded-xl p-4 border border-[#D9A0A0]/30">
                  <AlertCircle className="w-4 h-4 text-[#9C4848] flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-[#5C2E2E] leading-relaxed">
                    <strong>Nota importante:</strong> Las respuestas indican señales que conviene evaluar pronto. La ortopedia funcional es más efectiva entre los 5 y 12 años, durante la etapa de crecimiento del niño.
                  </div>
                </div>
              )}

              <div className="mt-6 bg-gradient-to-br from-[#1A1C1A] to-[#2a2b29] text-[#FAFAFA] rounded-2xl p-6 border border-[#1A1C1A]">
                <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#7A9180] mb-2">
                  <Sparkles className="w-3.5 h-3.5" /> Beneficio del Quiz
                </div>
                <h5 className="font-display text-2xl">15% en primera valoración</h5>
                <p className="text-sm text-[#D9CDBC] mt-1">
                  Diagnóstico completo + plan personalizado para tu hijo/a.
                </p>
                <div className="mt-4 inline-flex items-center gap-2 bg-[#7A9180] text-white rounded-full px-4 py-1.5 text-xs font-medium tracking-widest">
                  Código: NIÑOS15
                </div>
              </div>

              <a
                href={buildWaKids(result, answers.edad?.v || "su edad")}
                target="_blank"
                rel="noreferrer"
                data-testid="kids-quiz-whatsapp-cta"
              >
                <Button className="mt-6 w-full rounded-full bg-[#7A9180] hover:bg-[#5d7a66] text-white h-14 text-sm uppercase tracking-[0.2em]">
                  <MessageCircle className="w-5 h-5 mr-2" /> Agendar valoración pediátrica
                </Button>
              </a>
            </div>
          )}
        </div>

        <div className="px-8 py-5 border-t border-[#E6E2DC] bg-[#FAFAFA] flex items-center justify-between">
          {!isResult ? (
            <>
              <Button
                variant="ghost"
                disabled={step === 0}
                onClick={() => setStep((s) => s - 1)}
                className="text-[#5C5E5C] hover:text-[#1A1C1A]"
                data-testid="kids-quiz-back"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Atrás
              </Button>
              <Button
                disabled={!canNext}
                onClick={() => setStep((s) => s + 1)}
                className="rounded-full bg-[#1A1C1A] hover:bg-[#7A9180] text-white h-11 px-7 text-xs uppercase tracking-[0.2em]"
                data-testid="kids-quiz-next"
              >
                {step === total - 1 ? "Ver resultado" : "Siguiente"} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={reset} className="text-[#5C5E5C]" data-testid="kids-quiz-restart">
                <RotateCcw className="w-4 h-4 mr-1" /> Empezar de nuevo
              </Button>
              <span className="text-xs text-[#5C5E5C]">Cupón válido en primera consulta</span>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function KidsQuizTrigger({ onClick }) {
  return (
    <button onClick={onClick} className="block w-full text-left group" data-testid="kids-quiz-open">
      <div className="bg-gradient-to-br from-[#E6F0E8] to-[#D6E5DA] rounded-3xl p-8 lg:p-12 border border-[#7A9180]/30 hover:border-[#7A9180] transition-all overflow-hidden relative">
        <div className="absolute -top-10 -right-10 w-48 h-48 bg-[#7A9180]/20 rounded-full blur-3xl pointer-events-none" />
        <div className="relative grid lg:grid-cols-12 gap-6 lg:gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#5d7a66] mb-4">
              <Smile className="w-3.5 h-3.5" /> Quiz para padres
            </div>
            <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-[#1A1C1A]">
              ¿Tu hijo necesita<br />ortopedia funcional?
            </h3>
            <p className="mt-4 text-[15px] text-[#5C5E5C] max-w-xl leading-relaxed">
              5 preguntas, 2 minutos. Identifica señales tempranas como{" "}
              <strong className="text-[#1A1C1A]">respiración bucal nocturna</strong>,{" "}
              <strong className="text-[#1A1C1A]">mordida cruzada</strong> y otros patrones que conviene evaluar antes de los 12 años.{" "}
              <strong className="text-[#5d7a66]">Recibe 15% en primera valoración.</strong>
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#7A9180] text-white px-7 h-12 text-xs uppercase tracking-[0.2em] group-hover:bg-[#5d7a66] transition-colors">
              Hacer el Quiz <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </div>
      </div>
    </button>
  );
}
